import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  // Article specific
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
  // Schema specific
  schemaType?: 'NewsArticle' | 'WebSite' | 'CollectionPage';
  preloadImage?: string;
}

const defaultTitle = 'Seba Hot News - সর্বশেষ খবর সবার আগে';
const defaultDescription = 'ইন্টারনেট ভিত্তিক বাংলা অনলাইন সংবাদ সেবা প্রদানকারী। সেবা হট নিউজ জাতীয়, রাজনীতি, খেলাধুলা এবং অন্যান্য সমস্ত সংবাদ প্রকাশ করে।';
const defaultImage = 'https://blogger.googleusercontent.com/img/a/AVvXsEjUwTZQvWJ5mtwWoSrDl1gZ4cNy8KJjGmEDuUX2FArP3-SzeRIg0XUCVlOft6wFwf-HomJ6irpfrJ3LL_nykDZ53j40TKM0Yf0v_IDsFeLQly2Pdqzi0BVIuITlczIzcdmoOgPpEEwt5qJ_PVP3GgheTTZKTAPoZ3CZryX0O4C8Uzm8CPdXAnjPIk2fQe8S=s600';
const siteUrl = window.location.origin;

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  publishedTime,
  modifiedTime,
  authorName = 'সেবা হট নিউজ',
  schemaType = 'WebSite',
  preloadImage,
}) => {
  const seoTitle = title ? `${title} | Seba Hot News` : defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoUrl = canonicalUrl || siteUrl;
  const seoImage = ogImage || defaultImage;

  // Generate Schema.org JSON-LD
  const generateSchema = () => {
    let schema: any = {
      '@context': 'https://schema.org',
    };

    if (schemaType === 'NewsArticle') {
      schema = {
        ...schema,
        '@type': 'NewsArticle',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': seoUrl,
        },
        headline: title || defaultTitle,
        image: [seoImage],
        datePublished: publishedTime || new Date().toISOString(),
        dateModified: modifiedTime || new Date().toISOString(),
        author: {
          '@type': 'Person',
          name: authorName,
          url: siteUrl,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Seba Hot News',
          logo: {
            '@type': 'ImageObject',
            url: defaultImage,
          },
        },
      };
    } else if (schemaType === 'WebSite') {
      schema = {
        ...schema,
        '@type': 'WebSite',
        url: siteUrl,
        name: 'Seba Hot News',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      };
    }

    return JSON.stringify(schema);
  };

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic HTML Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {preloadImage && <link rel="preload" as="image" href={preloadImage} fetchPriority="high" />}

      {/* Google Discover Optimization: High resolution image preview */}
      <meta name="robots" content="max-image-preview:large" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content="Seba Hot News" />
      <meta property="og:locale" content="bn_BD" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Article specific Meta Tags */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === 'article' && authorName && (
        <meta property="article:author" content={authorName} />
      )}

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {generateSchema()}
      </script>
    </Helmet>
  );
};
